
import { useEffect, useMemo, useState, useCallback } from "react";

const STORAGE_KEY = "aether_bookmarked_fixtures";
const CHANGE_EVENT = "aether-bookmarks-changed";

// Cap is generous to accommodate "bookmark a whole week" across multiple
// competitions. Bookmarks are local-only so the only real cost is render
// volume in the drawer — well under any localStorage budget.
export const MAX_BOOKMARKS = 100;

function safeRead() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function safeWrite(list) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
  } catch (e) {
    console.warn("[bookmarks] write failed:", e?.message);
  }
}

export function getBookmarks() {
  return safeRead();
}

export function isBookmarked(fixtureId) {
  return safeRead().some((b) => b.fixture_id === fixtureId);
}


export function addBookmark(entry) {
  if (!entry?.fixture_id) {
    return { ok: false, action: "rejected", reason: "invalid" };
  }
  const list = safeRead();
  if (list.some((b) => b.fixture_id === entry.fixture_id)) {
    return { ok: true, action: "exists" };
  }
  if (list.length >= MAX_BOOKMARKS) {
    return { ok: false, action: "rejected", reason: "max" };
  }
  list.push({ ...entry, bookmarked_at: new Date().toISOString() });
  safeWrite(list);
  return { ok: true, action: "added" };
}

export function removeBookmark(fixtureId) {
  if (!fixtureId) return { ok: false, action: "rejected", reason: "invalid" };
  const next = safeRead().filter((b) => b.fixture_id !== fixtureId);
  safeWrite(next);
  return { ok: true, action: "removed" };
}



export function toggleBookmark(entry) {
  if (!entry?.fixture_id) {
    return { ok: false, action: "rejected", reason: "invalid" };
  }
  if (isBookmarked(entry.fixture_id)) {
    return removeBookmark(entry.fixture_id);
  }
  return addBookmark(entry);
}

// Single-write bulk operations — used by "bookmark whole week" actions to
// avoid N localStorage round-trips and N dispatched events for one user
// gesture. Returns counts so callers can show a tidy toast.
export function addBookmarksBulk(entries) {
  const valid = (entries || []).filter((e) => e?.fixture_id);
  if (valid.length === 0) return { ok: true, added: 0, skipped: 0, capped: 0 };

  const list = safeRead();
  const existingIds = new Set(list.map((b) => b.fixture_id));
  const now = new Date().toISOString();
  let added = 0;
  let skipped = 0;
  let capped = 0;

  for (const entry of valid) {
    if (existingIds.has(entry.fixture_id)) {
      skipped += 1;
      continue;
    }
    if (list.length >= MAX_BOOKMARKS) {
      capped += 1;
      continue;
    }
    list.push({ ...entry, bookmarked_at: now });
    existingIds.add(entry.fixture_id);
    added += 1;
  }

  if (added > 0) safeWrite(list);
  return { ok: true, added, skipped, capped };
}

export function removeBookmarksBulk(fixtureIds) {
  const ids = new Set((fixtureIds || []).filter(Boolean));
  if (ids.size === 0) return { ok: true, removed: 0 };
  const before = safeRead();
  const next = before.filter((b) => !ids.has(b.fixture_id));
  const removed = before.length - next.length;
  if (removed > 0) safeWrite(next);
  return { ok: true, removed };
}

// Inspector for "is this entire week saved?" — used by the per-week toggle
// button to flip between "Bookmark week" and "Remove week".
export function weekBookmarkState(bookmarks, weekFixtureIds) {
  const ids = (weekFixtureIds || []).filter(Boolean);
  if (ids.length === 0) return "empty";
  const saved = new Set((bookmarks || []).map((b) => b.fixture_id));
  let hit = 0;
  for (const id of ids) if (saved.has(id)) hit += 1;
  if (hit === 0) return "none";
  if (hit === ids.length) return "all";
  return "partial";
}

// Stable week label — fixture_week is stored as a string but may be numeric
// or alphanumeric (e.g. "QF", "SF"). Sort numerically first, then by string.
export function compareWeeks(a, b) {
  const na = parseInt(a, 10);
  const nb = parseInt(b, 10);
  const aNum = Number.isFinite(na);
  const bNum = Number.isFinite(nb);
  if (aNum && bNum && na !== nb) return na - nb;
  if (aNum && !bNum) return -1;
  if (!aNum && bNum) return 1;
  return String(a).localeCompare(String(b));
}

// Group bookmarks by fixture_week. Returns an array of
// { week, items: Bookmark[] } in ascending week order. Items inside a week
// are sorted by kickoff ascending (so the next match of the week is first).
export function groupBookmarksByWeek(bookmarks) {
  const byWeek = new Map();
  for (const b of bookmarks || []) {
    const week = String(b.fixture_week || "—");
    if (!byWeek.has(week)) byWeek.set(week, []);
    byWeek.get(week).push(b);
  }

  const groups = Array.from(byWeek.entries()).map(([week, items]) => {
    items.sort((x, y) => {
      const dx = getKickoffDate(x)?.getTime() ?? Infinity;
      const dy = getKickoffDate(y)?.getTime() ?? Infinity;
      return dx - dy;
    });
    return { week, items };
  });

  groups.sort((a, b) => compareWeeks(a.week, b.week));
  return groups;
}

// Phase classifier shared between sidebar and bookmarks page so chips/labels
// stay consistent. LIVE window covers 90 min + half-time + injury time.
const LIVE_WINDOW_MS = 2.5 * 60 * 60 * 1000;
const FINISHED_STATUS_RE = /FT|FINISHED|FULL[ _]?TIME|ENDED|POSTPONED|CANCELLED|ABANDONED/i;

export function getFixturePhase(fixture, now = Date.now()) {
  const status = String(fixture?.fixture_status || "").toUpperCase();
  if (FINISHED_STATUS_RE.test(status)) return "finished";
  const d = getKickoffDate(fixture);
  if (!d) return "upcoming";
  const delta = d.getTime() - now;
  if (delta > 0) return "upcoming";
  if (delta >= -LIVE_WINDOW_MS) return "live";
  return "finished";
}


export function useBookmarkedFixtures() {
  const [list, setList] = useState(() => safeRead());

  useEffect(() => {
    const refresh = () => setList(safeRead());

    const onStorage = (e) => {
      // Only react to changes on our key; ignore other localStorage activity.
      if (e.key === null || e.key === STORAGE_KEY) refresh();
    };

    window.addEventListener("storage", onStorage);
    window.addEventListener(CHANGE_EVENT, refresh);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(CHANGE_EVENT, refresh);
    };
  }, []);

  const toggle = useCallback((entry) => toggleBookmark(entry), []);
  const addBulk = useCallback((entries) => addBookmarksBulk(entries), []);
  const removeBulk = useCallback((ids) => removeBookmarksBulk(ids), []);

  const groupedByWeek = useMemo(() => groupBookmarksByWeek(list), [list]);

  return {
    bookmarks: list,
    groupedByWeek,
    toggle,
    isBookmarked,
    addBookmark,
    removeBookmark,
    addBulk,
    removeBulk,
  };
}


export function getKickoffDate(fixture) {
  if (!fixture?.fixture_date || !fixture?.kick_off_time) return null;
  const date = String(fixture.fixture_date).replace(/Z$/i, "").split("T")[0];
  const time = String(fixture.kick_off_time).replace(/Z$/i, "").slice(0, 8);
  const d = new Date(`${date}T${time}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}


export function formatKickoffCountdown(fixture, now = Date.now()) {
  const d = getKickoffDate(fixture);
  if (!d) return "—";
  const delta = d.getTime() - now;
  if (delta <= 0) return "Started";

  const totalMinutes = Math.floor(delta / 60_000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0 || days > 0) parts.push(`${hours}h`);
  parts.push(`${minutes}m`);
  return parts.join(" ");
}
